export const calculateAlzheimerDiagnosticCriteriaA = (doc: IAlzheimer): 'A+' | 'A-' | undefined => {
  const val = doc.functionalImpairment?.declinedForSixMonths;
  if (!val) return undefined;
  return val === 'yes' ? 'A+' : 'A-';
};

export const calculateAlzheimerDiagnosticCriteriaB = (doc: IAlzheimer): 'B+' | 'B-' | undefined => {
  const { amnestic, visualProcessing, language, atypical } = doc.cognitiveImpairment ?? {};
  const anyYes = [amnestic, visualProcessing, language, atypical].includes('yes');
  const anyUndefined = !amnestic || !visualProcessing || !language || !atypical;
  return anyYes ? 'B+' : anyUndefined ? undefined : 'B-';
};

export const calculateAlzheimerDiagnosticCriteriaC = (doc: IAlzheimer): 'C+' | 'C-' | 'C?' | undefined => {
  const { reducedBAmyloidInCSF, positiveAmyloidPET } = doc.amyloidMarkers ?? {};
  if (reducedBAmyloidInCSF === undefined || positiveAmyloidPET === undefined) {
    return undefined;
  }

  if (reducedBAmyloidInCSF === 'yes' || positiveAmyloidPET === 'yes') {
    return 'C+';
  }

  const isNotDone = (val: TYesNoNotDone) => val === 'notDone';
  const isNo = (val: TYesNoNotDone) => val === 'no';

  if (
    (isNotDone(reducedBAmyloidInCSF) && isNo(positiveAmyloidPET)) ||
    (isNo(reducedBAmyloidInCSF) && isNotDone(positiveAmyloidPET)) ||
    (isNotDone(reducedBAmyloidInCSF) && isNotDone(positiveAmyloidPET))
  ) {
    return 'C?';
  }

  return 'C-';
};

export const calculateAlzheimerDiagnosticCriteriaD = (doc: IAlzheimer): 'D+' | 'D-' | 'D?' | undefined => {
  const val = doc.tauChanges?.elevatedFosTau;
  if (val === undefined) return undefined;
  if (val === 'yes') return 'D+';
  if (val === 'no') return 'D-';
  return 'D?';
};

const interpretSupportingFactors = (
  name: keyof NonNullable<IAlzheimer['supportingFactors']>,
  value: TYesNoUnknown | NonNullable<IAlzheimer['supportingFactors']>['apoEGenotype'],
): 1 | -1 | 0 => {
  switch (name) {
    case 'apoEGenotype':
      return value && value !== 'unknown' ? (['2/4', '3/4', '4/4'].includes(value) ? 1 : -1) : 0;
    default:
      return value === 'yes' ? 1 : value === 'no' ? -1 : 0;
  }
};

export const calculateAlzheimerDiagnosticCriteriaE = (doc: IAlzheimer): number | undefined => {
  const { familyHistory, apoEGenotype, hippocampalAtrophy, abnormalFDGPET, elevatedNeurofilamentOrTau, otherTau } =
    doc.supportingFactors ?? {};

  if (!familyHistory || !hippocampalAtrophy || !abnormalFDGPET || !elevatedNeurofilamentOrTau || !otherTau) {
    return undefined;
  }

  return (
    interpretSupportingFactors('familyHistory', familyHistory) +
    interpretSupportingFactors('apoEGenotype', apoEGenotype) +
    interpretSupportingFactors('hippocampalAtrophy', hippocampalAtrophy) +
    interpretSupportingFactors('abnormalFDGPET', abnormalFDGPET) +
    interpretSupportingFactors('elevatedNeurofilamentOrTau', elevatedNeurofilamentOrTau) +
    interpretSupportingFactors('otherTau', otherTau)
  );
};

export const calculateAlzheimerDiagnosticCriteriaF = (doc: IAlzheimer): number | undefined => {
  const {
    untreatedSleepDisorder,
    untreatedPsychiatricDisorder,
    substanceUse,
    cognitiveAffectingMedications,
    lowCognitiveBaseline,
    inflammatoryOrMalignancyFindings,
  } = doc.redFlags ?? {};

  if (
    !untreatedSleepDisorder ||
    !untreatedPsychiatricDisorder ||
    !substanceUse ||
    !cognitiveAffectingMedications ||
    !lowCognitiveBaseline ||
    !inflammatoryOrMalignancyFindings
  ) {
    return undefined;
  }

  const interpret = (val: TYesNo) => (val === 'yes' ? 1 : 0);

  return (
    interpret(untreatedSleepDisorder) +
    interpret(untreatedPsychiatricDisorder) +
    interpret(substanceUse) +
    interpret(cognitiveAffectingMedications) +
    interpret(lowCognitiveBaseline) +
    interpret(inflammatoryOrMalignancyFindings)
  );
};

type TInterpretAlzheimerDiagnosticCriteriaABCDParams = [
  a: 'A+' | 'A-',
  b: 'B+' | 'B-',
  c?: 'C+' | 'C-' | 'C?',
  d?: 'D+' | 'D-' | 'D?',
];

export function interpretAlzheimerDiagnosticCriteriaABCD(
  ...params: TInterpretAlzheimerDiagnosticCriteriaABCDParams
): string;
export function interpretAlzheimerDiagnosticCriteriaABCD(doc?: IAlzheimer): string;
export function interpretAlzheimerDiagnosticCriteriaABCD(
  ...args: [IAlzheimer?] | TInterpretAlzheimerDiagnosticCriteriaABCDParams
): string | undefined {
  let a: string | undefined, b: string | undefined, c: string | undefined, d: string | undefined;

  if (args.length === 1 && typeof args[0] === 'object') {
    a = calculateAlzheimerDiagnosticCriteriaA(args[0]);
    b = calculateAlzheimerDiagnosticCriteriaB(args[0]);
    c = calculateAlzheimerDiagnosticCriteriaC(args[0]);
    d = calculateAlzheimerDiagnosticCriteriaD(args[0]);
  } else {
    [a, b, c, d] = args as TInterpretAlzheimerDiagnosticCriteriaABCDParams;
  }

  if (!a || !b) return undefined;

  // A-, B- and either C+ or D+ --> No Alzheimer's but risk
  if (a === 'A-' && b === 'B-' && (c === 'C+' || d === 'D+')) {
    return 'noAlzheimersButRisk';
  }

  // A-, B+ regardless of C or D --> MCI
  if (a === 'A-' && b === 'B+') {
    return 'mildCognitiveImpairment';
  }

  // A+, B- regardless of C or D --> unlikely, cannot diagnose
  if (a === 'A+' && b === 'B-') {
    return 'unlikelyAlzheimersDiagnosisCannotBeSet';
  }

  // All four positive --> definite Alzheimer's
  if (a === 'A+' && b === 'B+' && c === 'C+' && d === 'D+') {
    return 'alzheimersCriteriaMet';
  }

  // A+, B+, C+, D? --> probable, tau recommended
  if (a === 'A+' && b === 'B+' && c === 'C+' && d === 'D?') {
    return 'probableAlzheimersTauRecommended';
  }

  // A+, B+, C+, D- --> probable, check alternatives
  if (a === 'A+' && b === 'B+' && c === 'C+' && d === 'D-') {
    return 'probableAlzheimersCheckAlternatives';
  }

  // A+, B+, C?, D+ --> possible, amyloid recommended
  if (a === 'A+' && b === 'B+' && c === 'C?' && d === 'D+') {
    return 'possibleAlzheimersAmyloidRecommended';
  }

  // A+, B+, C-, D+ --> possible, amyloid required
  if (a === 'A+' && b === 'B+' && c === 'C-' && d === 'D+') {
    return 'possibleAlzheimersAmyloidRequired';
  }

  // A+, B+, C-, D- --> highly unlikely
  if (a === 'A+' && b === 'B+' && c === 'C-' && d === 'D-') {
    return 'highlyUnlikelyAlzheimers';
  }

  // A+, B+, C-, D? --> unlikely
  if (a === 'A+' && b === 'B+' && c === 'C-' && d === 'D?') {
    return 'unlikelyAlzheimers';
  }

  // A+, B+, C?, D- --> unlikely
  if (a === 'A+' && b === 'B+' && c === 'C?' && d === 'D-') {
    return 'unlikelyAlzheimers';
  }

  // A+, B+, C?, D? --> cannot diagnose
  if (a === 'A+' && b === 'B+' && c === 'C?' && d === 'D?') {
    return 'diagnosisCannotBeMade';
  }

  // Default fallback if none match
  return undefined;
}
